/**
 * Pricing block — interactivity for all section types:
 *  - sidebar section switching (multi-section layout)
 *  - webhosting/vps period toggle (Týdně / Měsíčně / Ročně)
 *  - web-management group tabs + Splide plan carousel
 *  - web-development range slider
 *  - domains availability check (POST /api/check-domain.php — mocked server-side)
 */
export function pricing(Splide) {
  document.querySelectorAll('[data-pricing]').forEach((root) => initPricing(root, Splide));
}

function initPricing(root, Splide) {
  sidebarNav(root);
  periodToggles(root);
  webManagement(root, Splide);
  webDevelopment(root);
  domainCheck(root);
}

/* ---- Sidebar: scroll to a section + scrollspy highlight ---- */
function sidebarNav(root) {
  const navItems = root.querySelectorAll('[data-pricing-nav]');
  if (!navItems.length) return;
  const panels = root.querySelectorAll('[data-pricing-panel]');

  // Click → smooth-scroll to the matching panel (scroll-margin-top clears the header).
  navItems.forEach((item) => {
    item.addEventListener('click', () => {
      const panel = root.querySelector(`[data-pricing-panel="${item.dataset.pricingNav}"]`);
      panel?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  // Scrollspy → highlight the section currently near the top.
  const setActive = (id) => navItems.forEach((n) => n.classList.toggle('is-active', n.dataset.pricingNav === id));
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.dataset.pricingPanel);
        });
      },
      { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
    );
    panels.forEach((p) => io.observe(p));
  }
}

/* ---- Period toggle ---- */
function periodToggles(root) {
  root.querySelectorAll('[data-pricing-toggle]').forEach((toggle) => {
    const scope = toggle.closest('[data-pricing-panel]') || root;
    const btns = toggle.querySelectorAll('[data-period]');

    const apply = (period) => {
      btns.forEach((b) => b.classList.toggle('is-active', b.dataset.period === period));
      scope.querySelectorAll('[data-price]').forEach((el) => {
        const v = el.dataset['price' + cap(period)];
        if (v != null) el.textContent = v;
      });
      scope.querySelectorAll('[data-price-suffix]').forEach((el) => {
        const v = el.dataset['suffix' + cap(period)];
        if (v != null) el.textContent = v;
      });
    };

    btns.forEach((b) => b.addEventListener('click', () => apply(b.dataset.period)));
  });
}

/* ---- Web management: tabs + carousel ---- */
function webManagement(root, Splide) {
  root.querySelectorAll('[data-pricing-mgmt]').forEach((mgmt) => {
    const slms = new Map();

    const mountSplide = (el) => {
      if (slms.has(el)) return slms.get(el);
      const splide = new Splide(el, {
        perPage: 3,
        gap: 0,
        arrows: false,
        pagination: false,
        drag: true,
        breakpoints: {
            1024: { perPage: 2 },
            640: { perPage: 1 },
        },
      });
      splide.mount();
      slms.set(el, splide);
      return splide;
    };

    const groups = mgmt.querySelectorAll('[data-mgmt-group]');
    const activeSplide = () => {
      const g = mgmt.querySelector('[data-mgmt-group]:not([hidden])');
      return g ? slms.get(g.querySelector('[data-mgmt-splide]')) : null;
    };

    // Mount the initially visible group; lazily mount others on tab switch.
    groups.forEach((g) => {
      if (!g.hidden) mountSplide(g.querySelector('[data-mgmt-splide]'));
    });

    mgmt.querySelectorAll('[data-mgmt-tab]').forEach((tab) => {
      tab.addEventListener('click', () => {
        const idx = tab.dataset.mgmtTab;
        mgmt.querySelectorAll('[data-mgmt-tab]').forEach((t) => t.classList.toggle('is-active', t === tab));
        groups.forEach((g) => {
          const show = g.dataset.mgmtGroup === idx;
          g.hidden = !show;
          g.classList.toggle('is-active', show);
          if (show) {
            const splide = mountSplide(g.querySelector('[data-mgmt-splide]'));
            splide.refresh();
          }
        });
      });
    });

    mgmt.querySelector('[data-mgmt-prev]')?.addEventListener('click', () => activeSplide()?.go('<'));
    mgmt.querySelector('[data-mgmt-next]')?.addEventListener('click', () => activeSplide()?.go('>'));
  });
}

/* ---- Web development: range slider ---- */
function webDevelopment(root) {
  root.querySelectorAll('[data-pricing-dev]').forEach((dev) => {
    const range = dev.querySelector('[data-dev-range]');
    if (!range) return;
    const steps = dev.querySelectorAll('[data-dev-step]');
    const ticks = dev.querySelectorAll('[data-dev-tick]');

    const paint = () => {
      const v = parseInt(range.value, 10);
      const max = parseInt(range.max, 10) || 1;
      range.style.setProperty('--dev-progress', `${(v / max) * 100}%`);
      steps.forEach((s) => {
        const on = parseInt(s.dataset.devStep, 10) === v;
        s.hidden = !on;
        s.classList.toggle('is-active', on);
      });
      ticks.forEach((t) => t.classList.toggle('is-on', parseInt(t.dataset.devTick, 10) <= v));
    };

    range.addEventListener('input', paint);
    paint();
  });
}

/* ---- Domains: availability check (mock) ---- */
function domainCheck(root) {
  root.querySelectorAll('[data-pricing-domains]').forEach((box) => {
    const input = box.querySelector('[data-domain-input]');
    const tld = box.querySelector('[data-domain-tld]');
    const btn = box.querySelector('[data-domain-check]');
    const result = box.querySelector('[data-domain-result]');
    if (!btn || !input || !result) return;

    const run = async () => {
      const sld = (input.value || '').trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
      if (!sld) {
        input.focus();
        return;
      }
      const ext = tld ? tld.value : '';
      btn.disabled = true;
      btn.classList.add('is-loading');
      try {
        const r = await fetch('/api/check-domain.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ domain: sld, tld: ext }),
        });
        const data = await r.json();
        if (!r.ok || !data.ok) throw new Error(data && data.error ? data.error : 'error');
        renderResult(result, data, `${sld}.${ext}`);
      } catch (e) {
        result.hidden = false;
        result.className = 'pricing-domains__result is-error';
        result.textContent = result.dataset.errorText || 'Error';
      } finally {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }
    };

    btn.addEventListener('click', run);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') run();
    });
  });
}

function renderResult(el, data, fqdn) {
  const available = !!data.available;
  el.hidden = false;
  el.className = 'pricing-domains__result ' + (available ? 'is-available' : 'is-taken');
  const label = available
    ? el.dataset.availableText || 'Available'
    : el.dataset.takenText || 'Taken';
  const price = data.price != null ? `<span class="pricing-domains__result-price">${data.price}</span>` : '';
  el.innerHTML = `
    <span class="pricing-domains__result-icon" aria-hidden="true">${available ? '✓' : '✕'}</span>
    <span class="pricing-domains__result-label">${label}</span>
    <span class="pricing-domains__result-domain">${escapeHtml(fqdn)}</span>
    ${price}`;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function cap(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
